import React, { useState, useEffect } from 'react';
import { Calendar, AlertCircle, RefreshCw } from 'lucide-react';
import SkeletonRecordCard from '../common/SkeletonRecordCard';
import Button from '../ui/Button';
import RecordStoryCard from '../ui/RecordStoryCard';

import LoadMoreButton from '../ui/LoadMoreButton';
import { setWeChatShare, SHARE_TEMPLATE } from '../../utils/wechat';
import { attendanceService } from '../../services';
import apiClient from '../../services/apiClient';
import { API_ENDPOINTS } from '../../config/api';
import usePermissionStore from '../../stores/permissionStore';


import { convertToProxyUrl } from '../../utils/imageUrl';

const StudentHomeView = ({ onSearch, searchParams = {}, searchTrigger = 0 }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastLoadTime, setLastLoadTime] = useState(0); // 上次加载时间

  const mainRef = React.useRef(null); // main容器的引用
  const sentinelRef = React.useRef(null); // 滚动加载哨兵

  // 获取用户角色信息
  const { isStudent, user, studentId, role, isLoggedIn } = usePermissionStore();

  // 获取学生姓名（优先从记录中获取，否则使用用户名）
  const studentName = records.length > 0 && records[0].student_name
    ? records[0].student_name
    : (user?.username || '');

  // 加载学生自己的上课记录
  const loadRecords = async (pageNum = 1, append = false, isRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      // 调试日志
      console.log('📚 StudentHomeView - 开始加载上课记录');
      console.log('📚 用户信息:', { user, studentId, role, isStudentRole: isStudent() });

      let response;
      const baseParams = {
        page: pageNum,
        per_page: 5,
        ...searchParams,
      };

      // 如果是手动刷新，带上标识
      if (isRefresh) {
        baseParams.is_refresh = 'true';
      }

      // 检查是否有访问令牌
      const accessToken = localStorage.getItem('access_token');

      // 学生角色且没有访问令牌时，使用 /me/attendance-records 端点
      if (!accessToken && isStudent()) {
        console.log('📚 学生角色 - 请求参数:', baseParams);
        console.log('📚 请求URL:', API_ENDPOINTS.AUTH.ME_ATTENDANCE_RECORDS);

        response = await apiClient.get(API_ENDPOINTS.AUTH.ME_ATTENDANCE_RECORDS, baseParams);
        console.log('📚 学生角色 - API响应:', response);
      } else {
        // 其他角色或具有访问令牌，使用通用的 attendance 服务
        console.log('📚 非学生角色/AccessToken - 请求参数:', baseParams);
        response = await attendanceService.getList(baseParams);
        console.log('📚 非学生角色/AccessToken - API响应:', response);
      }

      const items = (response.items || []).filter(record => record.status !== 'VOIDED');
      console.log('📚 解析到的记录数量:', items.length);
      console.log('📚 记录详情:', items);

      if (items.length > 0 && accessToken) {
        // 如果是令牌模式，且 store 中还没有学生ID，则从记录中提取学生ID保存到 store
        if (!studentId) {
          usePermissionStore.getState().setStudentId(items[0].student_id);
        }
        // 保存一个临时的名字到本地，方便个人中心展示
        if (items[0].student_name) {
          localStorage.setItem('access_student_name', items[0].student_name);
        }
      }

      if (append) {
        setRecords(prev => [...prev, ...items]);
      } else {
        setRecords(items);
      }

      setHasMore(response.has_next || false);
      setPage(pageNum);

      // 更新上次加载时间
      setLastLoadTime(Date.now());
    } catch (err) {
      console.error('❌ 加载上课记录失败:', err);
      console.error('❌ 错误详情:', { message: err.message, response: err.response });
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      // 注意：不在这里重置 isAppending，让滚动恢复 useEffect 处理后再重置
    }
  };

  // 初始加载
  useEffect(() => {
    // 如果还没登录且没有令牌，先不加载，等待 App.jsx 处理认证
    const accessToken = localStorage.getItem('access_token');
    if (!isLoggedIn && !accessToken) {
      console.log('📚 StudentHomeView - 尚未认证，推迟加载');
      return;
    }
    
    console.log('📚 StudentHomeView - useEffect触发，开始加载');
    loadRecords();
  }, [searchTrigger, isLoggedIn]); // 依赖 isLoggedIn，确保登录后触发加载

  // 设置分享内容
  useEffect(() => {
    if (studentName || isLoggedIn) {
      const shareTitle = studentName 
        ? SHARE_TEMPLATE(studentName)
        : '我在夏天艺术工作室，遇见更好的我们 😄😄';
      
      const shareDesc = studentName
        ? `这里记录了 ${studentName} 在夏天艺术工作室的每一个进步瞬间，诚邀您共同见证。`
        : '在艺术的阳光下，像夏日一样茁壮成长。';

      const shareImg = (records.length > 0 && records[0].session_artwork_photos_urls?.[0]) || null;
      
      console.log('📢 StudentHomeView - 更新微信分享配置:', { studentName, shareTitle, shareImg });

      setWeChatShare({
        uiTitle: studentName ? `${studentName} 夏天艺术工作室` : '夏天艺术工作室',
        title: shareTitle,
        desc: shareDesc,
        imgUrl: convertToProxyUrl(shareImg)
      });
    }
  }, [studentName, records, isLoggedIn]);

  // 使用 IntersectionObserver 监听滚动到底部
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !isLoadingMore && !loading && hasMore && !error) {
          const now = Date.now();
          // 2秒冷却时间
          if (now - lastLoadTime < 2000) {
            console.log('⏳ 加载冷却中，请稍后...');
            return;
          }

          console.log('📥 触发向下加载更多 (IntersectionObserver)');
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [hasMore, isLoadingMore, loading, page, lastLoadTime]);

  // 加载更多
  const handleLoadMore = () => {
    if (!loading && !isLoadingMore && hasMore) {
      setIsLoadingMore(true);
      loadRecords(page + 1, true);
    }
  };

  return (
    <div className="h-full bg-gray-100 flex flex-col relative">
      {/* 顶部导航已移除 */}

      {/* Content */}
      <main ref={mainRef} className="flex-1 p-4 space-y-3 overflow-y-auto">
        {/* 错误提示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">加载失败</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
              <div className="flex gap-3 mt-3">
                <button
                  onClick={() => {
                    apiClient.resetCircuit();
                    loadRecords(1);
                  }}
                  className="text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors"
                >
                  点击重试
                </button>
                {error.includes('认证已过期') && (
                  <button
                    onClick={() => window.location.reload()}
                    className="text-xs px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg font-medium hover:bg-blue-200 transition-colors"
                  >
                    重新刷新
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 加载中 - 使用 Skeleton 骨架屏 */}
        {loading && records.length === 0 && (
          <div className="space-y-6">
            <SkeletonRecordCard />
            <SkeletonRecordCard />
            <SkeletonRecordCard />
          </div>
        )}

        {/* 记录列表 - 课后报告卡片风格 */}
        <div className="space-y-6">
          {records.map((record) => {
            // 格式化时间范围
            const formatTimeRange = (rec) => {
              if (rec.start_time && rec.end_time) {
                return `${rec.start_time} - ${rec.end_time}`;
              }
              return '';
            };

            // 获取日期字段 - 兼容不同的字段名
            const rawDate = record.date || record.attendance_date || record.session_date || record.created_at;

            // 鲁棒的日期解析函数
            const getParsedDate = (val) => {
              if (!val) return null;
              const s = val.toString().trim();

              // 1. 尝试标准 ISO 格式
              let d = new Date(s.replace(' ', 'T'));
              if (!isNaN(d.getTime())) return d;

              // 2. 尝试手动解析 YYYY-MM-DD
              const parts = s.split(/[ \-T:/]/);
              if (parts.length >= 3) {
                const y = parseInt(parts[0]);
                const m = parseInt(parts[1]) - 1;
                const dy = parseInt(parts[2]);
                if (y > 1900 && m >= 0 && m <= 11 && dy >= 1 && dy <= 31) {
                  return new Date(y, m, dy);
                }
              }
              return null;
            };

            const dateObj = getParsedDate(rawDate);
            const year = dateObj ? dateObj.getFullYear() : '2026';
            const month = dateObj ? (dateObj.getMonth() + 1) : '??';
            const day = dateObj ? dateObj.getDate().toString().padStart(2, '0') : '??';
            const weekDays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
            const week = dateObj ? weekDays[dateObj.getDay()] : '';

            return (
              <div key={record.id} className="bg-white rounded-[2.5rem] overflow-hidden shadow-md border border-gray-100/50 mb-8 transition-all hover:shadow-lg">
                {/* 报告头 - iOS 风格紧凑单行 */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '11px 18px',
                    gap: '10px',
                    borderBottom: '1px solid #f1f5f9',
                    background: '#fff',
                  }}
                >
                  {/* 课程缩写 tag */}
                  <div
                    style={{
                      background: '#2563EB',
                      borderRadius: '9px',
                      padding: '5px 10px',
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 800,
                        letterSpacing: '0.02em',
                        lineHeight: 1,
                      }}
                    >
                      {(record.course_name || '艺术').slice(0, 2)}
                    </span>
                  </div>

                  {/* 日期主体 */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', flex: 1 }}>
                    <span
                      style={{
                        fontSize: '22px',
                        fontWeight: 800,
                        color: '#1e293b',
                        lineHeight: 1,
                        letterSpacing: '-0.03em',
                      }}
                    >
                      {day}
                    </span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#475569',
                        lineHeight: 1,
                      }}
                    >
                      {month}月 {year}
                    </span>
                  </div>

                  {/* 星期 pill */}
                  <div
                    style={{
                      background: '#f1f5f9',
                      borderRadius: '7px',
                      padding: '3px 9px',
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#64748b',
                        letterSpacing: '0.02em',
                        lineHeight: 1,
                      }}
                    >
                      {week}
                    </span>
                  </div>
                </div>

                {/* 图片 + 教师点评一体化组件 */}
                <RecordStoryCard
                  artworkPhotos={record.session_artwork_photos_urls || []}
                  classroomPhotos={record.classroom_photos_urls || []}
                  remarks={record.remarks || ''}
                />
              </div>
            );
          })}
        </div>

        {/* 空状态 */}
        {!loading && !error && records.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Calendar size={48} className="text-gray-300 mb-3" />
            <p className="text-sm text-[#718096]">暂无上课记录</p>
          </div>
        )}

        {/* 加载更多哨兵 */}
        <div ref={sentinelRef} className="h-4 w-full" />

        {/* 加载更多 */}
        <div className="pb-8">
          <LoadMoreButton
            onClick={handleLoadMore}
            loading={loading || isLoadingMore}
            hasMore={hasMore && records.length > 0}
          />
        </div>

        {/* ICP 备案信息 */}
        <div className="pb-20 text-center">
          <a 
            href="https://beian.miit.gov.cn/" 
            target="_blank" 
            rel="noreferrer" 
            className="text-[10px] text-gray-400/50 hover:text-gray-400 transition-colors"
          >
            浙ICP备2026033504号
          </a>
        </div>
      </main>


    </div>
  );
};

export default StudentHomeView;